import { Flex, Icon, Typography } from '@aloft/toolkit-sui';

import { useCallback, useRef } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { AddressInput } from '../../AddressInput/AddressInput.js';
import { AddressInputErrors } from '../../AddressInput/AddressInputErrors.js';
import { MLSInput } from '../../MLSInput/MLSInput.js';
import MapBox from '../../map/MapBox.ts';
import type { Coordinates } from '../../map/types';
import { useMap } from '../../map/useMap.ts';
import { useUpdateSubjectPin } from '../../map/useUpdateSubjectPin.ts';
import { EditPropertyDetailsForm } from './types.js';

const DEFAULT_COORDINATES = { lat: 39.8283, lng: -98.5795 }; // Center of the US as a fallback when no coordinates are available

type EditPropertyDetailsModalProps = {
  form: UseFormReturn<z.input<EditPropertyDetailsForm>, unknown, z.infer<EditPropertyDetailsForm>>;
} & { addressLabel: string; showWarning: boolean; lockAddress: boolean };

export const EditPropertyDetailsModalContent = ({
  form,
  addressLabel,
  showWarning,
  lockAddress,
}: EditPropertyDetailsModalProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapBox | null>(null);

  const currentCoordinates = form.watch('coordinates');
  const mapCoordinates: Coordinates | undefined = currentCoordinates
    ? { lat: currentCoordinates.latitude, lng: currentCoordinates.longitude }
    : undefined;

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const onMapClickHandler = (e: any) => {
      const existingLayers = ['pins', 'subject'].filter((layerId) => map.getLayer(layerId));
      if (existingLayers.length > 0) {
        const features = map.queryRenderedFeatures(e.point, { layers: existingLayers });
        if (features.length > 0) return;
      }
      form.setValue(
        'coordinates',
        { latitude: e.lngLat.lat, longitude: e.lngLat.lng },
        { shouldDirty: true, shouldValidate: true },
      );
    };

    const onMouseMove = (e: any) => {
      const existingLayers = ['pins', 'subject'].filter((layerId) => map.getLayer(layerId));
      let isOverFeature = false;
      if (existingLayers.length > 0) {
        const features = map.queryRenderedFeatures(e.point, { layers: existingLayers });
        isOverFeature = features.length > 0;
      }
      map.getCanvas().style.cursor = isOverFeature ? 'pointer' : 'crosshair';
    };

    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    map.on('click', onMapClickHandler);
    map.on('mousemove', onMouseMove);
    map.on('mouseleave', onMouseLeave);
  }, [form]);

  useMap({
    mapContainerRef,
    mapRef,
    center: mapCoordinates ?? DEFAULT_COORDINATES,
    zoom: 15,
    callbacks: {
      onMapLoad: handleMapLoad,
    },
  });

  useUpdateSubjectPin({
    mapRef,
    coordinates: mapCoordinates,
  });

  return (
    <Flex width="100%" height="100%" gap={4} flexDirection="column">
      {showWarning && (
        <Flex alignItems="center" gap={1} marginBottom="1rem" marginLeft=".75rem">
          <Icon
            icon="error-warning"
            size="md"
            color="textWarning"
            __dangerDoNotUseSx={{ marginTop: '3px' }}
          />
          <Typography>
            Changing the MLS info {lockAddress ? '' : ' or address'} may overwrite existing data for
            this property.
          </Typography>
        </Flex>
      )}
      <FormProvider {...form}>
        <AddressInput addressLabel={addressLabel} lockAddress={lockAddress} />
      </FormProvider>
      <Flex gap={2}>
        <FormProvider {...form}>
          <MLSInput />
        </FormProvider>
      </Flex>
      <FormProvider {...form}>
        <AddressInputErrors />
      </FormProvider>
      <Flex flexDirection="column">
        <Typography variant="h3">Verify Location</Typography>
        <Typography variant="body1" color="textSecondary">
          Click anywhere on the map to place to pin on the correct location
        </Typography>
        <Flex
          ref={mapContainerRef}
          height="250px"
          width="100%"
          position="relative"
          marginTop="16px"
          borderRadius="4px"
        />
      </Flex>
    </Flex>
  );
};
